import { ButtonVariant, FieldAction, MaterialColors } from "@/core/types/types";
import {
  JSXElementConstructor,
  ReactElement,
  ReactFragment,
  useEffect,
  useState,
} from "react";
import { FieldProps } from "..";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import { useClaimCheck } from "@/core/hooks/useClaimCheck/useClaimCheck";
import { useFormContext } from "react-hook-form";
import {
  CustomizedButton as ReusableButtonComponent,
  CustomizedButtonProps,
} from "@flightright/reusable-components";
import { mutate } from "swr";

const DATA_CY = "data-cy";
const DATA_NAV = "data-nav";
const NEW_LINE = "\n";
const FAT_BTN_STYLES = {
  height: "auto",
  whiteSpace: "break-spaces",
};

interface NavigationButtonProps extends CustomizedButtonProps {
  endIcon?:
    | string
    | ReactElement<any, string | JSXElementConstructor<any>>
    | ReactFragment
    | undefined;
  redirectUrl?: string;
  buttonText?: string;
  variant?: ButtonVariant;
  style: {} | null;
  [DATA_CY]?: "submit" | "skip" | "cancel-step";
  [DATA_NAV]?: "cancel-step";
}

const DEFAULT_COLOR: CustomizedButtonProps["color"] = "inherit";

const getButtonVariant = (
  variant: ButtonVariant | undefined,
  isCancelStep: boolean,
  isSkipStep: boolean,
  isSubmitWithNoValidationStep: boolean
) => {
  if (variant) return variant;
  if (isCancelStep || isSubmitWithNoValidationStep) return ButtonVariant.Text;
  if (isSkipStep) return ButtonVariant.Outlined;
  return ButtonVariant.Contained;
};

const NavigationButton = ({
  field,
  startIcon,
  endIcon,
  subtext,
  redirectUrl,
  requiredFieldsAreFilled,
  buttonText = "",
}: FieldProps & Partial<NavigationButtonProps>) => {
  const {
    goToPreviousStep,
    submitStep,
    reset: resetInstance,
  } = useClaimCheck();
  const { getValues, reset, handleSubmit } = useFormContext();
  const icons = { startIcon, endIcon };
  const isCancelStep: boolean =
    field.properties?.action === FieldAction.CancelStep;
  const isNavigationCancelStep: boolean =
    field.properties?.navigationAction === FieldAction.CancelStep;
  const isSubmitWithNoValidationStep: boolean =
    field.properties?.action === FieldAction.SubmitNoValidation;
  const isSkipStep: boolean = field.properties?.action === FieldAction.SkipStep;
  const isSubmit: boolean =
    field.action === FieldAction.Submit &&
    field.properties?.action === undefined;
  const isReset: boolean = field.properties?.action === FieldAction.Reset;
  const variant: ButtonVariant | undefined = field.properties?.variant;
  const buttonVariant: ButtonVariant = getButtonVariant(
    variant,
    isCancelStep,
    isSkipStep,
    isSubmitWithNoValidationStep
  );
  const labelText: string = t(field.label) || buttonText;
  const isFatBtn: boolean = labelText.indexOf(NEW_LINE) > -1;

  const goBack = async () => {
    goToPreviousStep();
  };

  const resetSession = async () => {
    // clear SWR cache
    await mutate((_) => true, undefined, { revalidate: false });
    resetInstance();
  };

  const skipStep = async () => {
    // Get array of registered fields
    const formValues = Object.keys(getValues());
    // Reset all values to null and clear errors to allow form submission
    const resetValues = Object.fromEntries(
      formValues.map((key) => [key, null])
    );
    reset(resetValues);
    // Submit updated form values to Camunda
    handleSubmit(submitStep)();
  };

  const redirectPage = async () => {
    const urlToRedirect = field.properties?.redirectUrl || redirectUrl || "";
    try {
      new URL(urlToRedirect);
      window.location.href = urlToRedirect;
    } catch (e) {
      return;
    }
  };

  const getButtonColor = (): NavigationButtonProps["color"] => {
    if (!isSubmit || redirectUrl) {
      return isSkipStep || isCancelStep || redirectUrl
        ? MaterialColors.Primary
        : undefined;
    }

    return requiredFieldsAreFilled ? undefined : DEFAULT_COLOR;
  };

  const getCypressDataAttribute = (): NavigationButtonProps["data-cy"] => {
    if (isSubmit) return "submit";
    if (isSkipStep) return "skip";
    if (isCancelStep) return "cancel-step";
    return undefined;
  };

  const getNavigationActionAttribute =
    (): NavigationButtonProps["data-nav"] => {
      if (isNavigationCancelStep) return "cancel-step";
      return undefined;
    };

  const [color, setColor] =
    useState<NavigationButtonProps["color"]>(DEFAULT_COLOR);

  useEffect(() => {
    const newColor = getButtonColor();
    setColor(newColor);
  }, [requiredFieldsAreFilled]);

  const buttonProps: NavigationButtonProps = {
    [DATA_CY]: getCypressDataAttribute(),
    [DATA_NAV]: getNavigationActionAttribute(),
    type: isSubmit ? "submit" : undefined,
    color: color,
    variant: buttonVariant,
    onClick: () => {
      if (redirectUrl) {
        redirectPage();
      }
      if (isCancelStep) {
        goBack();
      } else if (isSkipStep) {
        skipStep();
      } else if (isSubmitWithNoValidationStep) {
        skipStep();
      } else if (isReset) {
        resetSession();
      } else {
        return undefined;
      }
    },
    label: labelText,
    subtext,
    style: isFatBtn ? { ...FAT_BTN_STYLES } : null,
    ...icons,
  };

  return <ReusableButtonComponent {...buttonProps} />;
};

export default NavigationButton;
